import React, { useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  CircleCheckBig,
  ExternalLink,
  FlaskConical,
  Layers,
  Search,
  ShieldCheck,
  Target,
} from "lucide-react";
import type {
  AssuranceAssessment,
  AssuranceFinding,
  AssuranceTraceStep,
} from "../../../assurance/types";
import { selectCampaign } from "../../../assurance/model";
import {
  SUPPRESSION_REASONS,
  suppressFinding,
  unsuppressFinding,
} from "../../../assurance/data";
import { SeverityBadge, type Severity } from "../../ui/SeverityBadge";
import { cn } from "../../../lib/utils";
import {
  CaveatNote,
  DomainBadge,
  EmptyPanel,
  FindingLink,
  KeyValueGrid,
  MetricTile,
  NotMeasured,
  Pill,
  SectionPanel,
  StatusPill,
  ViewEvidenceButton,
  fmtCurrency,
  fmtDate,
  fmtDateTime,
  fmtNumber,
} from "../primitives";
import { SEVERITY_BG, SEVERITY_ORDER } from "./ExecutiveViews";

// ---------------------------------------------------------------------------
// Findings inventory
// ---------------------------------------------------------------------------

type SortKey = "severity" | "score" | "domain" | "status";

const severityRank: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

/**
 * Walkable source-to-sink trace.
 *
 * A static list of steps makes the reader hold the whole path in their head.
 * Stepping through it one hop at a time — with the source and sink named
 * explicitly — is how a reviewer actually follows tainted data, and it is the
 * part of a deep-analysis finding that proves the result is not a guess.
 */
function TaintStepper({
  steps,
  isTaint,
}: {
  steps: AssuranceTraceStep[];
  isTaint: boolean;
}) {
  const [active, setActive] = React.useState(0);
  const current = Math.min(active, steps.length - 1);

  const roleOf = (index: number): { label: string; tone: string } | null => {
    if (!isTaint) return null;
    if (index === 0) return { label: "Source", tone: "bg-critical/12 text-critical ring-critical/25" };
    if (index === steps.length - 1) return { label: "Sink", tone: "bg-high/12 text-high ring-high/25" };
    return { label: "Propagation", tone: "bg-surface2 text-muted ring-border" };
  };

  return (
    <div className="space-y-3">
      {isTaint && steps.length > 1 && (
        <div className="flex items-center justify-between gap-2 border-b border-border/70 pb-2">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setActive((i) => Math.max(0, i - 1))}
              disabled={current === 0}
              className="btn-ghost px-2 py-1 text-[11px] disabled:opacity-40"
              aria-label="Previous step in the data-flow trace"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setActive((i) => Math.min(steps.length - 1, i + 1))}
              disabled={current === steps.length - 1}
              className="btn-ghost px-2 py-1 text-[11px] disabled:opacity-40"
              aria-label="Next step in the data-flow trace"
            >
              Next
            </button>
          </div>
          <span className="text-[11px] font-medium tabular-nums text-muted">
            Step {current + 1} of {steps.length}
          </span>
        </div>
      )}

      <ol className="space-y-0">
        {steps.map((step, stepIndex) => {
          const role = roleOf(stepIndex);
          const isActive = isTaint && stepIndex === current;
          return (
            <li key={stepIndex} className="relative flex gap-3 pb-4 last:pb-0">
              {stepIndex < steps.length - 1 && (
                <span className="absolute left-[9px] top-5 h-full w-px bg-border" aria-hidden="true" />
              )}
              <span
                className={cn(
                  "z-[1] mt-0.5 flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full text-[10px] font-bold ring-1 transition-colors",
                  isActive
                    ? "bg-brand text-white ring-brand"
                    : "bg-brand/10 text-brand ring-brand/25"
                )}
              >
                {stepIndex + 1}
              </span>
              <button
                type="button"
                onClick={() => setActive(stepIndex)}
                aria-current={isActive ? "step" : undefined}
                className={cn(
                  "min-w-0 flex-1 rounded-lg px-2 py-1 text-left text-xs leading-5 transition-colors",
                  isTaint ? "cursor-pointer hover:bg-surface2/60" : "cursor-default",
                  isActive && "bg-surface2/80 ring-1 ring-brand/20"
                )}
              >
                <span className="flex flex-wrap items-center gap-1.5">
                  {step.step && <span className="font-semibold text-text">{step.step}</span>}
                  {role && (
                    <span className={cn("rounded-full px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide ring-1", role.tone)}>
                      {role.label}
                    </span>
                  )}
                </span>
                {step.detail && <span className="mt-0.5 block text-text2">{step.detail}</span>}
                {step.code && (
                  <span className="mt-1 block overflow-x-auto rounded border border-border/60 bg-surface0 px-2 py-1 font-mono text-[10.5px] leading-4 text-text">
                    {step.code}
                  </span>
                )}
                {step.file && (
                  <span className="mt-0.5 block truncate font-mono text-[10.5px] text-muted">
                    {step.file}
                    {step.line ? `:${step.line}` : ""}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * Waive a finding with a recorded, categorised rationale.
 *
 * A consultant triaging hundreds of findings needs to record *why* something
 * was dismissed, not just that it was — the category is what makes the waiver
 * reviewable months later, and the reason is what a client asks about.
 */
function SuppressControl({ finding, demo }: { finding: AssuranceFinding; demo: boolean }) {
  const [open, setOpen] = React.useState(false);
  const [category, setCategory] = React.useState(SUPPRESSION_REASONS[0].value);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [done, setDone] = React.useState<string | null>(null);

  const suppressed = finding.status?.toLowerCase() === "suppressed";

  if (demo) return null;

  const run = async (fn: () => Promise<unknown>, label: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setDone(label);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <p className="text-xs text-success">
        {done} Reload the assessment to see the updated status.
      </p>
    );
  }

  if (suppressed) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted">
          This finding is suppressed{finding.evidence && typeof finding.evidence === "object" && "suppressionReason" in (finding.evidence as Record<string, unknown>)
            ? ""
            : ""}.
        </p>
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => unsuppressFinding(finding.id), "Suppression lifted.")}
          className="btn-ghost px-2 py-1 text-xs disabled:opacity-50"
        >
          {busy ? "Reopening…" : "Reopen finding"}
        </button>
        {error && <p className="text-xs text-critical">{error}</p>}
      </div>
    );
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="btn-ghost px-2 py-1 text-xs">
        Suppress with reason
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-border/70 bg-surface1 p-3">
      <label className="block text-[11px] font-semibold text-text2" htmlFor="suppress-category">
        Reason category
      </label>
      <select
        id="suppress-category"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        className="w-full rounded-md border border-border bg-surface0 px-2 py-1 text-xs text-text"
      >
        {SUPPRESSION_REASONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <label className="block text-[11px] font-semibold text-text2" htmlFor="suppress-reason">
        Rationale
      </label>
      <textarea
        id="suppress-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        placeholder="What did you verify that makes this safe to waive?"
        className="w-full rounded-md border border-border bg-surface0 px-2 py-1 text-xs text-text"
      />
      {error && <p className="text-xs text-critical">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy || reason.trim().length < 5}
          onClick={() => run(() => suppressFinding(finding.id, category, reason.trim()), "Finding suppressed.")}
          className="btn-primary px-2.5 py-1 text-xs disabled:opacity-50"
        >
          {busy ? "Saving…" : "Suppress"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-2 py-1 text-xs">
          Cancel
        </button>
      </div>
      <p className="text-[10.5px] leading-4 text-muted">
        The rationale and your identity are recorded with the finding and appear in generated reports.
      </p>
    </div>
  );
}

/** How close a vulnerable dependency sits to shipped, running code. */
const EXPOSURE_LABEL: Record<string, string> = {
  invoked: "Reachable",
  imported: "Imported by application code",
  not_imported: "Not imported",
  development: "Development/test only",
};

/** Canonical OWASP Top 10 (2021) names so different engines group consistently. */
const OWASP_2021: Record<string, string> = {
  A01: "Broken Access Control",
  A02: "Cryptographic Failures",
  A03: "Injection",
  A04: "Insecure Design",
  A05: "Security Misconfiguration",
  A06: "Vulnerable and Outdated Components",
  A07: "Identification and Authentication Failures",
  A08: "Software and Data Integrity Failures",
  A09: "Security Logging and Monitoring Failures",
  A10: "Server-Side Request Forgery (SSRF)",
};

/** Extract the canonical OWASP category id (e.g. "A03") from a finding's tags. */
function owaspCategory(finding: AssuranceFinding): string | null {
  for (const tag of finding.owasp || []) {
    const match = String(tag).match(/A(\d{1,2})/i);
    if (match) {
      const id = `A${match[1].padStart(2, "0")}`;
      if (OWASP_2021[id]) return id;
    }
  }
  return null;
}

export function FindingsInventoryView({
  assessment,
  demo,
  selectedFindingId,
  presetSeverity,
  onSelectFinding,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  selectedFindingId?: string;
  presetSeverity?: string;
  onSelectFinding: (finding: AssuranceFinding, openEvidence?: boolean) => void;
}) {
  const findings = assessment.findings;
  const [search, setSearch] = useState("");
  const [domain, setDomain] = useState("all");
  const [severity, setSeverity] = useState(
    presetSeverity && (SEVERITY_ORDER as string[]).includes(presetSeverity)
      ? presetSeverity
      : "all"
  );
  const [status, setStatus] = useState("all");
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [confidence, setConfidence] = useState("all");
  const [groupByOwasp, setGroupByOwasp] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("severity");
  const [sortAsc, setSortAsc] = useState(true);

  const domains = useMemo(
    () => Array.from(new Set(findings.map((finding) => finding.domain))).sort(),
    [findings]
  );
  const statuses = useMemo(
    () => Array.from(new Set(findings.map((finding) => finding.status))).sort(),
    [findings]
  );
  // Only engines that report a rule-declared confidence populate this filter.
  const confidences = useMemo(
    () =>
      Array.from(
        new Set(findings.map((finding) => finding.confidence).filter(Boolean))
      ).sort() as string[],
    [findings]
  );
  const hasOwasp = useMemo(
    () => findings.some((finding) => owaspCategory(finding)),
    [findings]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = findings.filter((finding) => {
      const matchesSearch =
        !query ||
        [
          finding.title,
          finding.id,
          finding.file,
          finding.tool,
          finding.domain,
          finding.cwe,
          finding.cve,
          finding.package,
          finding.owner,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      return (
        matchesSearch &&
        (domain === "all" || finding.domain === domain) &&
        (severity === "all" || finding.severity === severity) &&
        (status === "all" || finding.status === status) &&
        (confidence === "all" || finding.confidence === confidence) &&
        (!priorityOnly || finding.priorityEvidence)
      );
    });
    const direction = sortAsc ? 1 : -1;
    return [...matches].sort((a, b) => {
      switch (sortKey) {
        case "severity":
          return (severityRank[a.severity] - severityRank[b.severity]) * direction;
        case "score":
          return ((b.score ?? -1) - (a.score ?? -1)) * direction;
        case "domain":
          return a.domain.localeCompare(b.domain) * direction;
        case "status":
          return a.status.localeCompare(b.status) * direction;
        default:
          return 0;
      }
    });
  }, [findings, search, domain, severity, status, confidence, priorityOnly, sortKey, sortAsc]);

  // Grouped view: findings bucketed by canonical OWASP Top-10 category, keeping
  // the active sort inside each bucket. Findings without an OWASP tag land in
  // "Uncategorized" at the end.
  const owaspGroups = useMemo(() => {
    if (!groupByOwasp) return null;
    const buckets = new Map<string, AssuranceFinding[]>();
    for (const finding of filtered) {
      const key = owaspCategory(finding) || "other";
      const bucket = buckets.get(key);
      if (bucket) bucket.push(finding);
      else buckets.set(key, [finding]);
    }
    return [...buckets.entries()].sort(([a], [b]) => {
      if (a === "other") return 1;
      if (b === "other") return -1;
      return a.localeCompare(b);
    });
  }, [filtered, groupByOwasp]);

  const bySeverity = useMemo(() => {
    const counts = new Map<Severity, number>();
    for (const finding of filtered) {
      counts.set(finding.severity, (counts.get(finding.severity) || 0) + 1);
    }
    return counts;
  }, [filtered]);

  if (!findings.length) {
    return (
      <EmptyPanel
        demo={demo}
        title="No findings returned"
        detail="The assessment contains no findings — either nothing was detected or scanning has not run yet."
      />
    );
  }

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((current) => !current);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const SortHeader = ({ column, children }: { column: SortKey; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={() => toggleSort(column)}
      className="inline-flex items-center gap-1 uppercase tracking-[0.06em] hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
    >
      {children}
      {sortKey === column &&
        (sortAsc ? (
          <ArrowUp className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ArrowDown className="h-3 w-3" aria-hidden="true" />
        ))}
    </button>
  );

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="asr-panel p-3">
        <div
          className={cn(
            "grid gap-2.5",
            confidences.length
              ? "md:grid-cols-[minmax(0,1fr)_140px_130px_130px_130px]"
              : "md:grid-cols-[minmax(0,1fr)_150px_140px_140px]"
          )}
        >
          <label className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <span className="sr-only">Search findings</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="input py-2 pl-9 text-[13px]"
              placeholder="Search title, file, CWE, package, owner…"
            />
          </label>
          <select
            className="input py-2 text-[13px]"
            value={domain}
            onChange={(event) => setDomain(event.target.value)}
            aria-label="Filter by domain"
          >
            <option value="all">All domains</option>
            {domains.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <select
            className="input py-2 text-[13px]"
            value={severity}
            onChange={(event) => setSeverity(event.target.value)}
            aria-label="Filter by severity"
          >
            <option value="all">All severities</option>
            {SEVERITY_ORDER.map((item) => (
              <option key={item} value={item}>
                {item[0].toUpperCase() + item.slice(1)}
              </option>
            ))}
          </select>
          <select
            className="input py-2 text-[13px]"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            {statuses.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          {confidences.length > 0 && (
            <select
              className="input py-2 text-[13px]"
              value={confidence}
              onChange={(event) => setConfidence(event.target.value)}
              aria-label="Filter by detection confidence"
            >
              <option value="all">All confidence</option>
              {confidences.map((item) => (
                <option key={item} value={item}>
                  {item[0].toUpperCase() + item.slice(1)} confidence
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-text2">
              <input
                type="checkbox"
                checked={priorityOnly}
                onChange={(event) => setPriorityOnly(event.target.checked)}
                className="h-3.5 w-3.5 rounded border-border accent-[rgb(var(--brand))]"
              />
              <Target className="h-3.5 w-3.5 text-brand" aria-hidden="true" />
              Priority evidence only
            </label>
            {hasOwasp && (
              <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-text2">
                <input
                  type="checkbox"
                  checked={groupByOwasp}
                  onChange={(event) => setGroupByOwasp(event.target.checked)}
                  className="h-3.5 w-3.5 rounded border-border accent-[rgb(var(--brand))]"
                />
                <Layers className="h-3.5 w-3.5 text-brand" aria-hidden="true" />
                Group by OWASP Top 10
              </label>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted">
            <span>
              {filtered.length.toLocaleString()} of {findings.length.toLocaleString()} findings
            </span>
            {SEVERITY_ORDER.filter((item) => bySeverity.get(item)).map((item) => (
              <span key={item} className="inline-flex items-center gap-1">
                <span className={cn("h-2 w-2 rounded-full", SEVERITY_BG[item])} aria-hidden="true" />
                <span className="font-semibold tabular-nums text-text2">
                  {bySeverity.get(item)}
                </span>
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="asr-panel overflow-hidden">
        <div className="max-h-[62vh] overflow-auto">
          <table className="asr-table">
            <thead>
              <tr>
                <th scope="col">
                  <SortHeader column="severity">Severity</SortHeader>
                </th>
                <th scope="col">Finding</th>
                <th scope="col">
                  <SortHeader column="domain">Domain</SortHeader>
                </th>
                <th scope="col">Location</th>
                <th scope="col">Owner</th>
                <th scope="col">
                  <SortHeader column="status">Status</SortHeader>
                </th>
                <th scope="col" className="text-right">
                  <SortHeader column="score">Score</SortHeader>
                </th>
                <th scope="col">
                  <span className="sr-only">Evidence</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(owaspGroups ?? [["all", filtered] as [string, AssuranceFinding[]]]).map(
                ([groupKey, groupList]) => (
                  <React.Fragment key={groupKey}>
                    {owaspGroups && (
                      <tr className="bg-surface2/50">
                        <td colSpan={8} className="py-2">
                          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-text2">
                            <Layers className="h-3.5 w-3.5 text-brand" aria-hidden="true" />
                            {groupKey === "other"
                              ? "No OWASP mapping"
                              : `${groupKey}:2021 — ${OWASP_2021[groupKey]}`}
                            <span className="font-semibold normal-case tracking-normal text-muted">
                              {groupList.length} finding{groupList.length === 1 ? "" : "s"}
                            </span>
                          </span>
                        </td>
                      </tr>
                    )}
                    {groupList.map((finding) => (
                <tr
                  key={finding.id}
                  className={cn(
                    "cursor-pointer",
                    selectedFindingId === finding.id && "bg-brand/5"
                  )}
                  onClick={() => onSelectFinding(finding)}
                >
                  <td className="whitespace-nowrap">
                    <SeverityBadge severity={finding.severity} size="sm" />
                  </td>
                  <td className="max-w-sm min-w-[220px]">
                    <div className="flex items-start gap-1.5">
                      {finding.priorityEvidence && (
                        <Target
                          className="mt-0.5 h-3.5 w-3.5 flex-none text-brand"
                          aria-label="Priority evidence"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="truncate font-medium text-text">{finding.title}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                          <FindingLink
                            id={finding.id}
                            finding={finding}
                            onOpen={(item) => onSelectFinding(item, true)}
                          />
                          {finding.cwe && (
                            <span className="font-mono text-[11px] text-muted">{finding.cwe}</span>
                          )}
                          {finding.cve && (
                            <span className="font-mono text-[11px] text-muted">{finding.cve}</span>
                          )}
                          {finding.malicious && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-critical/12 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-critical ring-1 ring-critical/30"
                              title="Reported as a malicious package — remove it; upgrading is not a fix"
                            >
                              Malicious package
                            </span>
                          )}
                          {finding.exposure === "development" && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-surface2 px-1.5 py-0.5 text-[10px] font-semibold text-muted ring-1 ring-border"
                              title={finding.exposureBasis}
                            >
                              dev/test only
                            </span>
                          )}
                          {(finding.corroboratedBy?.length ?? 0) >= 2 && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold text-success ring-1 ring-success/30"
                              title={`Independently flagged by ${finding.corroboratedBy!.join(" and ")}`}
                            >
                              <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                              {finding.corroboratedBy!.length}× corroborated
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap">
                    <DomainBadge domain={finding.domain} />
                  </td>
                  <td className="max-w-[240px]">
                    {finding.file ? (
                      <span className="block truncate font-mono text-[11.5px] text-text2" title={finding.file}>
                        {finding.file}
                        {finding.line ? `:${finding.line}` : ""}
                      </span>
                    ) : finding.package ? (
                      <span className="font-mono text-[11.5px] text-text2">
                        {finding.package}
                        {finding.version ? `@${finding.version}` : ""}
                      </span>
                    ) : (
                      <NotMeasured demo={demo} />
                    )}
                  </td>
                  <td className="whitespace-nowrap text-muted">{finding.owner || "—"}</td>
                  <td className="whitespace-nowrap">
                    <StatusPill status={finding.status} demo={demo} />
                  </td>
                  <td className="whitespace-nowrap text-right font-semibold tabular-nums text-text">
                    {finding.score ?? "—"}
                  </td>
                  <td className="text-right">
                    <ViewEvidenceButton
                      compact
                      onClick={() => onSelectFinding(finding, true)}
                    />
                  </td>
                </tr>
                    ))}
                  </React.Fragment>
                )
              )}
              {!filtered.length && (
                <tr>
                  <td colSpan={8}>
                    <div className="px-4 py-8 text-center text-xs text-muted">
                      No findings match the current filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Finding evidence
// ---------------------------------------------------------------------------

export function FindingEvidenceView({
  assessment,
  demo,
  selectedFindingId,
  onSelectFinding,
  onSelectView,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  selectedFindingId?: string;
  onSelectFinding: (finding: AssuranceFinding, openEvidence?: boolean) => void;
  onSelectView: (view: string, params?: Record<string, string>) => void;
}) {
  const findings = assessment.findings;
  const requestedIndex = findings.findIndex(
    (finding) => finding.id === selectedFindingId
  );

  if (selectedFindingId && requestedIndex === -1) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => onSelectView("findings")}
          className="btn-ghost px-2 py-1 text-xs"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to inventory
        </button>
        <EmptyPanel
          demo={demo}
          title="Finding not available in this assessment"
          detail={`No finding with id "${selectedFindingId}" exists in the current assessment. It may belong to a different repository, commit, or dataset.`}
        />
      </div>
    );
  }

  const index = Math.max(0, requestedIndex);
  const finding = findings[index];

  if (!finding) {
    return (
      <EmptyPanel
        demo={demo}
        title="No finding selected"
        detail="Open a finding from the inventory to inspect its full evidence chain."
      />
    );
  }

  const previous = findings[index - 1];
  const next = findings[index + 1];

  return (
    <div className="space-y-4">
      {/* Pager */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onSelectView("findings")}
          className="btn-ghost px-2 py-1 text-xs"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to inventory
        </button>
        <div className="flex items-center gap-1.5 text-xs text-muted">
          <span className="tabular-nums">
            {index + 1} / {findings.length}
          </span>
          <button
            type="button"
            className="btn-icon h-7 w-7"
            disabled={!previous}
            onClick={() => previous && onSelectFinding(previous, true)}
            aria-label="Previous finding"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn-icon h-7 w-7"
            disabled={!next}
            onClick={() => next && onSelectFinding(next, true)}
            aria-label="Next finding"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="asr-panel p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <SeverityBadge severity={finding.severity} />
          <DomainBadge domain={finding.domain} />
          <StatusPill status={finding.status} demo={demo} />
          {finding.priorityEvidence && (
            <Pill tone="brand" icon={<Target className="h-3 w-3" aria-hidden="true" />}>
              Priority evidence
            </Pill>
          )}
          {!demo && (
            <Pill tone="success" icon={<FlaskConical className="h-3 w-3" aria-hidden="true" />}>
              Scanner-reported finding
            </Pill>
          )}
          {demo && finding.evidenceClass && (
            <Pill
              tone={/current/i.test(finding.evidenceClass) ? "success" : "warning"}
              icon={<FlaskConical className="h-3 w-3" aria-hidden="true" />}
            >
              {finding.evidenceClass}
            </Pill>
          )}
        </div>
        <h3 className="mt-3 text-lg font-bold leading-snug tracking-tight text-text">
          {finding.title}
        </h3>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-muted">
          <span>{finding.id}</span>
          {finding.cwe && <span>{finding.cwe}</span>}
          {finding.cve && <span>{finding.cve}</span>}
          {finding.score !== undefined && (
            <span>
              score {finding.score}
              {finding.scoreModel && ` · ${finding.scoreModel}`}
            </span>
          )}
          {finding.confidence && <span>confidence {finding.confidence}</span>}
        </div>
        {finding.description && (
          <p className="mt-3 max-w-3xl text-sm leading-6 text-text2">{finding.description}</p>
        )}
      </div>

      {/* Scenario / impact */}
      {(finding.exploitScenario || finding.businessImpact || finding.exploitability) && (
        <div className="grid gap-4 xl:grid-cols-2">
          {(finding.exploitability || finding.exploitScenario) && (
            <SectionPanel title="Exploit path" description={finding.exploitability}>
              <p className="text-[13px] leading-6 text-text2">
                {finding.exploitScenario || finding.exploitability}
              </p>
              {(finding.source || finding.sink) && (
                <div className="mt-3 rounded-lg border border-border/70 bg-surface2/50 p-3 font-mono text-xs">
                  {finding.source && (
                    <div>
                      <span className="text-muted">source · </span>
                      <span className="text-text">{finding.source}</span>
                    </div>
                  )}
                  {finding.sink && (
                    <div className="mt-1">
                      <span className="text-muted">sink · </span>
                      <span className="text-critical">{finding.sink}</span>
                    </div>
                  )}
                </div>
              )}
            </SectionPanel>
          )}
          {finding.businessImpact && (
            <SectionPanel title="Business impact">
              <p className="text-[13px] leading-6 text-text2">{finding.businessImpact}</p>
              <div className="mt-3">
                <KeyValueGrid
                  demo={demo}
                  columns={2}
                  items={[
                    { label: "Owner", value: finding.owner },
                    { label: "SLA", value: finding.sla },
                  ]}
                />
              </div>
            </SectionPanel>
          )}
        </div>
      )}

      {/* Trace + evidence */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <SectionPanel
          title={finding.trace && finding.trace.length ? "Data-flow trace" : "Evidence chain"}
          description={
            finding.trace && finding.trace.length
              ? "Path from untrusted input to the vulnerable sink"
              : "Measured evidence linking detection to disposition"
          }
        >
          {(() => {
            const chain =
              finding.trace && finding.trace.length ? finding.trace : finding.evidenceChain || [];
            const isTaint = Boolean(finding.trace && finding.trace.length > 1);
            return chain.length ? (
              <TaintStepper steps={chain} isTaint={isTaint} />
            ) : (
              <EmptyPanel demo={demo} detail="No trace was recorded for this finding." />
            );
          })()}
        </SectionPanel>

        <div className="space-y-4">
          <SectionPanel
            title="Evidence"
            description={
              finding.file
                ? `${finding.file}${finding.line ? `:${finding.line}` : ""}`
                : undefined
            }
            actions={<SuppressControl finding={finding} demo={demo} />}
          >
            {finding.evidenceSnippet ? (
              <pre className="asr-code" tabIndex={0}>
                <code>{finding.evidenceSnippet}</code>
              </pre>
            ) : finding.evidence !== undefined ? (
              <pre className="asr-code" tabIndex={0}>
                <code>{JSON.stringify(finding.evidence, null, 2)}</code>
              </pre>
            ) : (
              <EmptyPanel demo={demo} />
            )}
            <div className="mt-3">
              <KeyValueGrid
                demo={demo}
                columns={3}
                items={[
                  { label: "Analysis capability", value: finding.capability || finding.tool },
                  { label: "First seen", value: fmtDate(finding.firstSeen) },
                  { label: "Last seen", value: fmtDate(finding.lastSeen) },
                  { label: "Introduced commit", value: finding.introducedCommit, mono: true },
                  { label: "Package", value: finding.package && `${finding.package}${finding.version ? `@${finding.version}` : ""}`, mono: true },
                  { label: "Fix version", value: finding.fixVersion, mono: true },
                  {
                    label: "Upgrade effort",
                    value:
                      finding.upgradeEffort &&
                      `${finding.upgradeEffort[0].toUpperCase()}${finding.upgradeEffort.slice(1)} — ${finding.upgradeJump} upgrade${
                        finding.version && finding.fixVersion
                          ? ` (${finding.version} → ${finding.fixVersion})`
                          : ""
                      }`,
                  },
                  {
                    label: "Dependency path",
                    value:
                      finding.dependencyPath && finding.dependencyPath.length > 1
                        ? `${finding.dependencyPath.join(" → ")}  (introduced by ${finding.introducedBy})`
                        : finding.dependencyRelationship === "direct"
                          ? "Declared directly in the project manifest"
                          : undefined,
                  },
                  {
                    label: "Exposure",
                    value: finding.exposure
                      ? `${EXPOSURE_LABEL[finding.exposure] || finding.exposure}${finding.exposureBasis ? ` — ${finding.exposureBasis}` : ""}`
                      : undefined,
                  },
                  {
                    label: "Usage sites",
                    value:
                      typeof finding.usageSites === "number"
                        ? finding.usageSites === 0
                          ? "No direct imports (transitive dependency)"
                          : `Imported in ${finding.usageSites} source file${finding.usageSites === 1 ? "" : "s"}`
                        : undefined,
                  },
                  {
                    label: "Also affects",
                    value:
                      finding.additionalLocations && finding.additionalLocations.length
                        ? finding.additionalLocations
                            .map((l) => `${l.file}${l.line ? `:${l.line}` : ""}`)
                            .join(" · ")
                        : undefined,
                    mono: true,
                  },
                ]}
              />
            </div>
          </SectionPanel>
        </div>
      </div>

      {/* Remediation + validation */}
      <div className="grid gap-4 xl:grid-cols-2">
        <SectionPanel title="Remediation">
          {finding.recommendation ? (
            <p className="text-[13px] leading-6 text-text2">{finding.recommendation}</p>
          ) : (
            <EmptyPanel demo={demo} />
          )}
          {finding.references && finding.references.length > 0 && (
            <ul className="mt-3 space-y-1 border-t border-divider/70 pt-3">
              {finding.references.map((reference, referenceIndex) => (
                <li key={referenceIndex}>
                  {reference.url ? (
                    <a
                      href={reference.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
                    >
                      {reference.title || reference.url}
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="text-xs text-text2">{reference.title}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SectionPanel>

        <SectionPanel
          title="Validation steps"
          description="How to confirm the fix actually closes the path"
        >
          {finding.validationSteps && finding.validationSteps.length ? (
            <ul className="space-y-2">
              {finding.validationSteps.map((step, stepIndex) => (
                <li key={stepIndex} className="flex items-start gap-2 text-[13px] leading-5 text-text2">
                  <CircleCheckBig className="mt-0.5 h-3.5 w-3.5 flex-none text-success" aria-hidden="true" />
                  {step}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyPanel demo={demo} detail="No validation steps were provided." />
          )}
        </SectionPanel>
      </div>

      {/* Audit trail */}
      {finding.auditTrail && finding.auditTrail.length > 0 && (
        <SectionPanel title="Audit trail" flush>
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Actor</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {finding.auditTrail.map((event, eventIndex) => (
                  <tr key={eventIndex}>
                    <td className="whitespace-nowrap text-muted">{fmtDateTime(event.at) || "—"}</td>
                    <td className="whitespace-nowrap">{event.actor || "—"}</td>
                    <td>{event.action || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionPanel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Remediation
// ---------------------------------------------------------------------------

export function RemediationView({
  assessment,
  demo,
  onSelectFindingId,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  onSelectFindingId: (findingId: string) => void;
}) {
  const campaign = selectCampaign(assessment);
  const findingById = new Map(assessment.findings.map((finding) => [finding.id, finding]));
  const totalEffort = campaign.phases.reduce(
    (total, phase) => total + (phase.effortDays ?? 0),
    0
  );
  const plannedOutcomes = campaign.phases.reduce(
    (total, phase) => total + phase.outcomes.length,
    0
  );

  return (
    <div className="space-y-4">
      {campaign.objective && (
        <CaveatNote tone="brand">
          <span className="font-semibold text-text">Campaign objective: </span>
          {campaign.objective}
        </CaveatNote>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricTile
          label="Planned outcomes"
          value={plannedOutcomes ? fmtNumber(plannedOutcomes) : undefined}
          demo={demo}
        />
        <MetricTile
          label="Estimated effort"
          value={totalEffort ? `${fmtNumber(totalEffort)} d` : undefined}
          demo={demo}
        />
        <MetricTile
          label="Budget estimate"
          value={fmtCurrency(campaign.budgetEstimate)}
          demo={demo}
        />
        <MetricTile
          label="Validated closed"
          value={undefined}
          sub="requires post-fix scan"
          demo={demo}
        />
      </div>

      <SectionPanel
        title="Workstreams"
        description="Accountable tracks with owners, linked findings, and success criteria"
      >
        {campaign.workstreams.length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {campaign.workstreams.map((workstream, index) => (
              <div
                key={workstream.name || index}
                className="rounded-lg border border-border/70 bg-surface2/40 p-3.5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="text-[13px] font-semibold text-text">{workstream.name}</div>
                  {workstream.owner && (
                    <Pill tone="neutral" className="flex-none">
                      {workstream.owner}
                    </Pill>
                  )}
                </div>
                {workstream.findings.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {workstream.findings.map((findingId) => (
                      <FindingLink
                        key={findingId}
                        id={findingId}
                        finding={findingById.get(findingId)}
                        onOpen={(item) => onSelectFindingId(item.id)}
                      />
                    ))}
                  </div>
                )}
                {workstream.success && (
                  <p className="mt-2.5 border-t border-divider/70 pt-2 text-xs leading-5 text-muted">
                    <span className="font-semibold text-text2">Done when: </span>
                    {workstream.success}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyPanel demo={demo} detail="No remediation workstreams were returned." />
        )}
      </SectionPanel>

      <SectionPanel
        title="Highest-priority fixes"
        description="Open findings ranked by contextual score"
        flush
      >
        {assessment.findings.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Severity</th>
                  <th scope="col">Finding</th>
                  <th scope="col">Owner</th>
                  <th scope="col">SLA</th>
                  <th scope="col">Fix</th>
                  <th scope="col"><span className="sr-only">Evidence</span></th>
                </tr>
              </thead>
              <tbody>
                {assessment.findings
                  .filter((finding) => finding.status.toLowerCase() === "open")
                  .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
                  .slice(0, 10)
                  .map((finding) => (
                    <tr
                      key={finding.id}
                      className="cursor-pointer"
                      onClick={() => onSelectFindingId(finding.id)}
                    >
                      <td className="whitespace-nowrap">
                        <SeverityBadge severity={finding.severity} size="sm" />
                      </td>
                      <td className="min-w-[260px] max-w-sm">
                        <div className="truncate font-medium text-text">{finding.title}</div>
                        <div className="mt-1">
                          <FindingLink
                            id={finding.id}
                            finding={finding}
                            onOpen={(item) => onSelectFindingId(item.id)}
                          />
                        </div>
                      </td>
                      <td className="whitespace-nowrap">{finding.owner || "—"}</td>
                      <td className="whitespace-nowrap">{finding.sla || "—"}</td>
                      <td className="max-w-md text-muted">
                        <span className="line-clamp-2">{finding.recommendation || "—"}</span>
                      </td>
                      <td className="text-right">
                        <ViewEvidenceButton
                          compact
                          onClick={() => onSelectFindingId(finding.id)}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} />
          </div>
        )}
      </SectionPanel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 30 / 60 / 90 campaign
// ---------------------------------------------------------------------------

export function CampaignView({
  assessment,
  demo,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
}) {
  const campaign = selectCampaign(assessment);

  if (!campaign.phases.length) {
    return (
      <EmptyPanel
        demo={demo}
        title="No campaign plan"
        detail="The assessment did not include a sequenced remediation campaign."
      />
    );
  }

  return (
    <div className="space-y-4">
      {campaign.objective && (
        <CaveatNote tone="brand">
          <span className="font-semibold text-text">
            {campaign.id ? `${campaign.id}: ` : ""}
          </span>
          {campaign.objective}
        </CaveatNote>
      )}

      <div className="grid gap-4 xl:grid-cols-3">
        {campaign.phases.map((phase, index) => (
          <SectionPanel
            key={phase.window || index}
            title={
              <span className="inline-flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold",
                    index === 0
                      ? "bg-critical/10 text-critical ring-1 ring-critical/25"
                      : index === 1
                        ? "bg-warning/10 text-warning ring-1 ring-warning/30"
                        : "bg-success/10 text-success ring-1 ring-success/25"
                  )}
                >
                  {index + 1}
                </span>
                Days {phase.window || "—"}
              </span>
            }
            description={
              phase.effortDays !== undefined
                ? `${fmtNumber(phase.effortDays)} engineering days`
                : undefined
            }
          >
            <ul className="space-y-2">
              {phase.outcomes.map((outcome, outcomeIndex) => (
                <li key={outcomeIndex} className="flex items-start gap-2 text-[13px] leading-5 text-text2">
                  <CircleCheckBig className="mt-0.5 h-3.5 w-3.5 flex-none text-muted" aria-hidden="true" />
                  {outcome}
                </li>
              ))}
              {!phase.outcomes.length && (
                <li className="text-xs text-muted">No outcomes recorded for this phase.</li>
              )}
            </ul>
          </SectionPanel>
        ))}
      </div>

      <CaveatNote>
        Campaign sequencing is a planning aid derived from assessment evidence.
        Closure is only recognized when a follow-up scan validates each fix.
      </CaveatNote>
    </div>
  );
}
